import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);

  useEffect(() => {
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const filteredComics = activeSlug === 'all'
    ? comics
    : comics.filter((c) => c.category === activeSlug);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const categoryTitle = activeSlug === 'all' ? '全部漫画' : currentCategory?.name || activeSlug;
  const categoryDesc = activeSlug === 'all'
    ? '浏览韩漫网站全部精彩韩国漫画作品'
    : `浏览韩漫网站${currentCategory?.name || ''}类型的韩国漫画作品`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - 韩漫网站`,
    description: categoryDesc,
    url: `https://www.hanman.site/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: 'https://www.hanman.site',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <Layout>
      <SEOHead
        title={`${categoryTitle} - 好看的韩国漫画 - 韩漫网站`}
        description={`${categoryDesc}，免费阅读，流畅下拉式浏览，智能加载技术确保极致体验。`}
        keywords={`韩漫,韩国漫画,${categoryTitle},韩漫免费阅读,下拉式韩漫`}
        canonicalUrl={`https://www.hanman.site/category/${activeSlug}`}
      />
      <SchemaOrg schema={categorySchema} />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: categoryTitle },
        ]}
      />

      <section className="w-full py-10 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {categoryTitle}
            </h1>
            <p className="text-sm text-foreground-500">{categoryDesc}</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-8 pb-6 border-b border-background-200">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                <i className="ri-emotion-sad-line text-4xl"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的漫画，敬请期待！</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      {comic.rating}
                    </div>
                    {comic.status === '已完结' && (
                      <div className="absolute top-2 left-2 bg-foreground-900/70 text-white text-xs px-2 py-0.5 rounded-full">
                        已完结
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <span className="text-white text-xs">{comic.chapters.length}话</span>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {comic.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filteredComics.length > 0 && (
            <div className="text-center mt-10">
              <p className="text-sm text-foreground-400">
                共 {filteredComics.length} 部作品
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}